import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import { AudioLines, History, Video } from 'lucide-react'
import * as React from 'react'
import type { ComponentType, ReactNode } from 'react'
import { ThemeToggle } from '@/components/theme'
import { cn } from '@/lib/utils'

/**
 * The frame every screen sits in.
 *
 * One exception, and it is deliberate: `/call/$code` renders WITHOUT this
 * chrome. A call is a full-attention surface — a nav bar offering "History"
 * beside someone's face is an invitation to navigate away mid-sentence and
 * drop the connection. `Shell` checks the route rather than each screen
 * opting in, so the exception is visible in one place.
 *
 * The frame is an application SHELL, not a document. `styles.css` stops the
 * document scrolling and this component owns the single region that does.
 * Three consequences worth knowing before editing it:
 *
 *   - the header and the tab bar are flex SIBLINGS of the scroller, not
 *     `position: fixed` chrome floating over it. Nothing can be scrolled past,
 *     nothing overlaps, and on iOS nothing has to be re-pinned as the URL bar
 *     or the keyboard changes the visual viewport. Fixed chrome over a
 *     scrolling document is where most of the "it's close but it doesn't fit"
 *     on a phone comes from.
 *   - the scroller is the ONLY element here with `overflow-y`, and it is
 *     `overscroll-contain`. That is what removes the rubber-band bounce, which
 *     is most of what makes a web app feel like a web page.
 *   - navigation is a BOTTOM TAB BAR below `sm` and the original top header at
 *     `sm` and up. Not one bar squeezed responsively into the other: on a
 *     phone the top of the screen is the far corner from the thumb, and the
 *     two things this app does should not be a reach.
 */
export function Shell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const isCall = pathname.startsWith('/call/')

  const scroller = React.useRef<HTMLElement>(null)

  /**
   * Reset the scroller on a forward navigation — and ONLY on one.
   *
   * With the document non-scrolling, the app's scroller is this <main> rather
   * than the window, and that changes what the router's scroll restoration
   * does. `router-core/scroll-restoration.js` carries element scroll positions
   * FORWARD across a push (`toElementEntries[sel] ??= fromElementEntries[sel]`)
   * on the assumption that a persistent scroller — a sidebar, say — should not
   * jump; and its usual reset-to-top is applied to `window`, which here never
   * moves. Without this, scrolling /history down and tapping "New call" lands
   * you on a home screen already scrolled most of the way down it. Verified in
   * a browser rather than deduced: the router's cache really does come back
   * holding `{"[data-scroll-restoration-id=\"app-scroll\"]":{"scrollY":500}}`
   * under the DESTINATION's key.
   *
   * The real fix is one line in `router.tsx` —
   * `scrollToTopSelectors: ['[data-scroll-restoration-id="app-scroll"]']` —
   * which is that carry-over's own documented opt-out, and which turns this
   * effect into a harmless no-op rather than something that has to be unpicked.
   *
   * A pop must be left alone: landing back where you were in the history list
   * is the entire reason `scrollRestoration` is on. Telling the two apart by
   * listening for `popstate` DOES NOT WORK, and the way it fails is worth
   * recording, because it looks like it works. The flag is set correctly and
   * early; the problem is that the router restores from its `onRendered` while
   * this runs from a commit, so the reset simply landed a few milliseconds
   * after a restore that had already put the right value back — measured as
   * `scroll=500 @25ms` then `scroll=0 @28ms`, which no amount of flag
   * correctness changes.
   *
   * So discriminate on VALUE, not on event order. Every push mints a fresh
   * `__TSR_key` on the history entry; a pop hands back one we have already
   * been to. Seeding the set with the key we mount on is what lets a reload
   * restore its position instead of being scrolled to the top by this.
   */
  const navKey = useRouterState({
    select: (state) => state.location.state.__TSR_key ?? state.location.href,
  })
  const visited = React.useRef<Set<string> | null>(null)
  if (visited.current === null) visited.current = new Set([navKey])

  React.useEffect(() => {
    const keys = visited.current
    if (!keys || keys.has(navKey)) return
    keys.add(navKey)
    const frame = requestAnimationFrame(() => scroller.current?.scrollTo({ top: 0 }))
    return () => cancelAnimationFrame(frame)
  }, [navKey])

  if (isCall) return <Outlet />

  // A tab stays lit for its whole SECTION, so opening a recording from the
  // history list does not leave the bar with nothing selected and no clue
  // where you are. Deliberately mobile-only: the desktop header's active
  // state is unchanged, because a tab bar is a claim about which section you
  // are in and a top nav is a link to a page.
  const inHistory = pathname === '/history' || pathname.startsWith('/session/')
  const inHome = pathname === '/'

  return (
    /* `h-full` of a `#root` that is 100dvh. The horizontal and top insets are
       applied once, here, so every descendant can lay out edge to edge and
       still be clear of the notch — in landscape that inset is on the SIDE,
       which is the case a top-only fix misses. The bottom inset is not applied
       here: on mobile the tab bar owns it so the bar's own background runs
       under the home indicator instead of leaving a strip of paper there, and
       at `sm` and up, where there is no tab bar, the frame takes it back. */
    <div
      className={cn(
        'flex h-full flex-col bg-paper',
        'pt-[var(--sat)] pr-[var(--sar)] pl-[var(--sal)] sm:pb-[var(--sab)]',
      )}
    >
      {/* Unchanged at `sm` and up, and gone below it. `sticky top-0` was
          dropped rather than kept as decoration: nothing scrolls underneath a
          header that is a sibling of the scroller, so it was inert. `relative
          z-40` keeps the stacking context it was relying on. */}
      <header className="relative z-40 hidden shrink-0 border-b border-line bg-paper/85 backdrop-blur-md sm:block">
        {/* `shrink-0` + `whitespace-nowrap` throughout: at 390px the brand was
            breaking to "era / meet" and the first nav item to "New / call",
            which reads as a broken layout rather than a narrow one. The bar has
            room for all of it on one line once nothing is allowed to wrap. */}
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-3 px-4 sm:gap-6 sm:px-6">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2.5 text-ink pointer-coarse:min-h-11"
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-fg">
              <AudioLines className="size-[1.05rem]" />
            </span>
            <span className="display text-[1.0625rem] whitespace-nowrap">era meet</span>
          </Link>

          <nav className="flex shrink-0 items-center gap-1">
            <NavLink to="/">New call</NavLink>
            <NavLink to="/history">History</NavLink>
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* The one scrolling region in the app.
          `data-scroll-restoration-id` gives the router a stable selector for
          it: without the attribute it identifies scrollers by an
          `nth-child` path, which silently re-points at the wrong element the
          first time anything is inserted above this one. */}
      <main
        ref={scroller}
        data-scroll-restoration-id="app-scroll"
        className="flex-1 overflow-y-auto overscroll-contain"
      >
        {/* `min-h-full` + `flex-1` on the content: this is what keeps the
            footer at the BOTTOM of a short page instead of floating directly
            under the content, which is how it behaved when it was a sibling of
            a `min-h-screen` column. Desktop is unchanged. */}
        <div className="flex min-h-full flex-col">
          {/* py-10 is 80px of a ~500px phone viewport spent on nothing. 24px
              below `sm`, the original above it. */}
          <div className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 sm:py-10">
            <Outlet />
          </div>

          {/* The footer moved INSIDE the scroller, which is the whole trick:
              below `sm` it costs zero viewport because it sits under the fold,
              and it is still there for anyone who scrolls to the end. At `sm`
              and up it behaves exactly as before.

              The first line is context for a reviewer; the second is a claim
              about what happens to your media. On a phone only the second one
              earns its space — and it earns it most there, because the phone is
              where somebody joins a call having read nothing else.

              THE SECOND LINE USED TO BE A LIE. It read "Only per-speaker audio,
              transcripts and conversation metrics are kept", which was true
              when this app could not retain video and false from the moment it
              could: a creator who picks Video gets a 720p MP4 kept as well, and
              this sentence went on denying it on every screen, including the
              lobby where somebody decides whether to join.

              It cannot be made conditional here. The shell renders on `/`,
              `/history`, `/session/$id` and `/j/$slug`, most of which have no
              single call to describe — so the fix is a sentence that is true
              for every call rather than one that guesses. What a specific call
              keeps is stated by the retention indicator inside that call, which
              is derived from the policy rather than from prose. */}
          <footer className="shrink-0 border-t border-line">
            <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-3 gap-y-1 px-4 py-4 text-xs text-ink-dim sm:px-6 sm:py-5">
              <span className="hidden sm:inline">A reference client for the ERA Stream API.</span>
              <span className="hidden sm:inline" aria-hidden>
                ·
              </span>
              <span>
                Everyone’s camera is live. What a call keeps — nothing, audio, or audio and video —
                is chosen per call and shown while you are in it.
              </span>
            </div>
          </footer>
        </div>
      </main>

      {/* The mobile tab bar.
          No wordmark above it, and that is the trade being made: a persistent
          "era meet" bar costs ~9% of an iPhone SE viewport on every screen to
          tell you the name of the app you just opened, while each screen's own
          Fraunces title already says where you are. This is what iOS apps do. */}
      <nav
        aria-label="Primary"
        className="flex shrink-0 items-stretch border-t border-line bg-paper/95 pb-[var(--sab)] backdrop-blur-md sm:hidden"
      >
        <TabLink to="/" icon={Video} label="New call" active={inHome} />
        <TabLink to="/history" icon={History} label="History" active={inHistory} />

        {/* The theme toggle's home on mobile.
            It is an action, not a destination, so it sits OUTSIDE the two tabs
            behind a hairline rather than becoming a third one — a tab bar that
            mixes the two stops being a statement about where you are. It is
            here rather than in a mobile header because a header would cost
            ~44px on every screen for a control used roughly once, and rather
            than in a settings screen because this app does not have one. It
            gets the far corner and the smallest target of the three, which is
            the correct order of priority for the rarest control.

            Sized up from the primitive's 36px `icon`: 36px is a miss target on
            a phone. Overriding here rather than in `ui/button.tsx` keeps the
            desktop header's density exactly as the operator has it. */}
        <div className="my-2 w-px shrink-0 bg-line" aria-hidden />
        <ThemeToggle className="h-[var(--tabbar-h)] w-14 shrink-0 rounded-none [&_svg]:size-5" />
      </nav>
    </div>
  )
}

/**
 * A bottom tab. Icon over label, full-height target, accent when active.
 *
 * Colour alone for the active state is the iOS convention and it is what the
 * palette in `styles.css` already reserves the accent for ("interactive and
 * brand … the active nav item"). No underline or pill: those are the Material
 * idiom, and this bar is trying to feel like the platform it is on.
 *
 * `active` is passed in rather than left to `activeProps` because a tab stays
 * lit for its whole section, which is more than the router's own match.
 */
function TabLink({
  to,
  icon: Icon,
  label,
  active,
}: {
  to: '/' | '/history'
  icon: ComponentType<{ className?: string }>
  label: string
  active: boolean
}) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        // The whole cell is the target: 56px tall by roughly half the screen
        // wide, against a 44x44 minimum.
        'flex min-h-[var(--tabbar-h)] flex-1 flex-col items-center justify-center gap-1 py-2',
        'text-[0.6875rem] font-medium whitespace-nowrap transition-colors',
        // `-webkit-tap-highlight-color` is off globally, so press feedback is
        // this component's own job now.
        'active:bg-surface-2',
        active ? 'text-accent' : 'text-ink-muted',
      )}
    >
      <Icon className="size-5" />
      <span>{label}</span>
    </Link>
  )
}

// Narrowed to the two paths that belong in the nav rather than `string`: the
// router is fully typed, and a `string` here would throw that away and let a
// typo'd path compile.
function NavLink({ to, children }: { to: '/' | '/history'; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center rounded-md px-2.5 py-1.5 text-sm font-medium whitespace-nowrap text-ink-muted transition-colors sm:px-3 pointer-coarse:min-h-11',
        'hover:bg-surface-2 hover:text-ink',
      )}
      activeOptions={{ exact: to === '/' }}
      activeProps={{ className: 'bg-surface-2 text-ink' }}
    >
      {children}
    </Link>
  )
}
